import { ReactNode, useEffect, useState } from "react";

import styled from "styled-components";

// Material
import { Box, Collapse, Grid, Skeleton, Stack, Tooltip, TooltipProps, Typography, tooltipClasses } from "@mui/material";

// Icons
import { ErrorOutlineOutlined } from "@mui/icons-material";

import Deletable from "./Deletable";
import CollapsibleButton from "./CollapsibleButton";

const StyledTooltip = styled(({ className, ...props }: TooltipProps) => <Tooltip {...props} classes={{ popper: className }} />)(() => ({
	[`& .${tooltipClasses.tooltip}`]: {
		backgroundColor: "#fff2f2",
		color: "#de4759",
		borderRadius: 5.7,
		border: "1px solid #e10000",
		fontSize: 14.5,
		fontFamily: "PT Sans Pro Narrow, sans-serif",
	},
}));

interface ICollapseAreaProps {
	id?: string;
	title?: string;
	tooltip?: boolean;
	tooltipText?: string;
	collapsible?: boolean;
	deletable?: boolean;
	valid?: boolean;
	readOnly?: boolean;
	loading?: boolean;
	openCollapse?: boolean;
	sx?: object;
	sxTitle?: object;
	onDeleteAction?: () => void;
	onToggleCollapse?: (collapsed: boolean, id?: string) => void;
	icon?: ReactNode;
	iconHover?: ReactNode;
	deleteTitle?: string;
	disabled?: boolean;
	children: ReactNode;
	autoCollapse?: boolean;
}

export default function CollapseArea(props: Readonly<ICollapseAreaProps>) {
	const [collapsed, setCollapsed] = useState(true);

	const sxTitle = props.sxTitle ?? {};

	const handleExpandToggle = () => {
		setCollapsed(!collapsed);
		if (props.onToggleCollapse) {
			props.onToggleCollapse(!collapsed, props.id);
		}
	};

	useEffect(() => {
		if (props.openCollapse) {
			setCollapsed(false);
		} else if (props?.autoCollapse) {
			setCollapsed(true);
		}
	}, [props.openCollapse, props?.autoCollapse]);

	return (
		<Box sx={props.sx ?? { padding: "32px 20px !important" }}>
			<Collapse
				className="section-container-content"
				style={{ paddingLeft: "15px" }}
				in={!collapsed}
				collapsedSize={40}
				timeout={100}
			>
				<Box sx={{ position: "relative" }}>
					<Grid container alignItems={"center"} sx={{ minHeight: "40px" }}>
						{Boolean(props.title) && (
							<Grid container item xs={12}>
								<Stack direction={"row"} alignContent={"center"} alignItems={"center"}>
									<Stack>
										{props.loading ? (
											<Skeleton variant="rectangular" width={400} height={25} sx={{ borderRadius: "6px" }} />
										) : (
											<>
												{props.tooltip ? (
													<StyledTooltip
														title={props.tooltipText}
														placement="right"
														className="regular-pt-sans-pro-narrow-label"
													>
														<Typography className={collapsed ? "page-title" : "page-title-blue"}>
															{props.title}
															{!props.valid && (
																<ErrorOutlineOutlined
																	sx={{ color: "red", fontSize: "15px", marginLeft: "5px" }}
																	fontSize="small"
																/>
															)}
														</Typography>
													</StyledTooltip>
												) : (
													<Typography
														className={collapsed ? "page-title" : "page-title-blue"}
														component="div"
														sx={{ ...sxTitle }}
														style={{ lineHeight: 1.1 }}
													>
														{props.title}
													</Typography>
												)}
											</>
										)}
									</Stack>
									<Stack direction={"row"} justifyContent={"space-between"}>
										<CollapsibleButton
											collapsible={props.collapsible}
											collapsed={collapsed}
											loading={props.loading}
											handleExpandToggle={handleExpandToggle}
										/>
										<Deletable
											deletable={props.deletable}
											loading={props.loading}
											deleteTitle={props.deleteTitle}
											icon={props.icon}
											iconHover={props.iconHover}
											onDeleteAction={props.onDeleteAction}
											readOnly={props.readOnly}
										/>
									</Stack>
								</Stack>
							</Grid>
						)}
					</Grid>

					<Grid container className="general-section-container">
						<Grid item xs={12}>
							{props.children}
						</Grid>
					</Grid>
				</Box>
			</Collapse>
		</Box>
	);
}
